"use employeeHours";

import { useState, useCallback, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "@/hooks";
import {
  createEmployeeWorkHour,
  deleteEmployeeWorkHour,
  fetchDaysEmployee,
  fetchEmployees,
  updateEmployeeWorkHour,
} from "@/store/modules/employees/thunks";
import { AppDispatch } from "@/store";

import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { employeeWorkHoursSchema } from "@/validations";
import { useAuth } from "@/context/AuthContext";

const useEmployeeWorkHours = (onSuccess?: () => void) => {
  const { params, setParams, handlePage } = useParams();
  const { establishment, baseURL } = useAuth();
  const { per_page, current_page } = params;

  const setEmployeeId = (id: number) => {
    // Limpar dados anteriores antes de definir o novo funcionário
    setIsEmployeeWorkHours(null);
    setDaysOfWeekId(undefined);

    // Limpar formulário completamente
    resetWorkHours();

    // Definir o novo employee_id após a limpeza
    setValue("employee_id", id, { shouldValidate: true });
  };

  // Controle de edição de horário existente
  const [editingWorkHour, setEditingWorkHour] = useState<any | null>(null);

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    getValues,
    watch,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: yupResolver(employeeWorkHoursSchema),
    defaultValues: {
      employee_id: 0, // Mudado de undefined para 0
      days: [
        {
          day_of_week_id: [],
          is_day_off: false,
          work_start: "",
          work_end: "",
          lunch_start: "",
          lunch_end: "",
        },
      ],
    },
  });

  // Função customizada para resetar completamente o formulário
  const resetWorkHours = () => {
    reset({
      employee_id: 0,
      days: [
        {
          day_of_week_id: [],
          is_day_off: false,
          work_start: "",
          work_end: "",
          lunch_start: "",
          lunch_end: "",
        },
      ],
    });
    // Limpar também os estados locais
    setIsEmployeeWorkHours(null);
    setDaysOfWeekId(undefined);
  };

  const setFormData = (data: any) => {
    // Temporariamente usando any para ignorar validação
    reset(data);
  };

  const onSubmit = async (data: any) => {
    // Temporariamente usando any para ignorar validação
    try {
      let result;

      if (editingWorkHour) {
        // Atualização de horário existente
        result = await dispatch(
          updateEmployeeWorkHour({
            workHourId: editingWorkHour.id,
            employeeId: editingWorkHour.employee_id,
            payload: data,
          }),
        );
      } else {
        // Criação
        result = await dispatch(createEmployeeWorkHour(data));
      }

      if (
        (!editingWorkHour && createEmployeeWorkHour.fulfilled.match(result)) ||
        (editingWorkHour && updateEmployeeWorkHour.fulfilled.match(result))
      ) {
        // Recarregar os dados completos após sucesso
        await dispatch(fetchEmployees({}));
        setEditingWorkHour(null);
      }
    } catch (error) {
      console.error("Erro ao enviar dados:", error);
    }
  };

  const { workHours, daysOfWeek, loading, error } = useSelector(
    (state: any) => state.employees,
  );
  const dispatch: AppDispatch = useDispatch();

  const [daysOfWeekId, setDaysOfWeekId] = useState<any>();
  const { employees } = useSelector((state: any) => state.employees);
  const [isEmployeeWorkHours, setIsEmployeeWorkHours] = useState<any>(null);

  useEffect(() => {
    // Aguarda establishment e baseURL estarem prontos antes de fazer requests
    if (!baseURL || !establishment) return;

    dispatch(fetchDaysEmployee({}));
  }, [dispatch, baseURL, establishment]);

  // Sincronizar isEmployeeWorkHours com o estado global dos employees
  useEffect(() => {
    const employeeId = watch("employee_id");

    // Se não há employee_id ou é 0, limpar os dados
    if (!employeeId || employeeId === 0) {
      setIsEmployeeWorkHours(null);
      return;
    }

    if (employees.data) {
      const selectedEmployee = employees.data.find(
        (emp: any) => emp.id == employeeId,
      );
      if (selectedEmployee && selectedEmployee.employee_work_hours) {
        setIsEmployeeWorkHours(selectedEmployee.employee_work_hours);
      } else {
        // Se o funcionário não tem horários específicos, limpar
        setIsEmployeeWorkHours(null);
      }
    }
  }, [watch("employee_id"), employees.data, employees.loading, watch]);

  // Atualizar quando workHours global mudar
  useEffect(() => {
    const employeeId = watch("employee_id");
    if (workHours && workHours.length > 0 && employeeId && employeeId !== 0) {
      setIsEmployeeWorkHours(workHours);
    }
  }, [workHours, watch("employee_id"), watch]);

  const handleDeleteWorkHour = async () => {
    try {
      const employeeId = watch("employee_id");
      if (!daysOfWeekId || !employeeId || employeeId === 0) {
        console.error("IDs necessários não estão definidos");
        return false;
      }

      const result = await dispatch(
        deleteEmployeeWorkHour({
          workHourId: daysOfWeekId,
          employeeId: employeeId,
        }),
      );

      if (deleteEmployeeWorkHour.fulfilled.match(result)) {
        // Recarregar os dados completos do funcionário após a remoção
        await dispatch(fetchEmployees({}));
        return true;
      }
      return false;
    } catch (error) {
      console.error("Erro ao deletar horário:", error);
      return false;
    }
  };

  const startEditWorkHour = (workHour: any) => {
    setEditingWorkHour(workHour);
    // Preenche o formulário com os dados do horário selecionado
    reset({
      employee_id: workHour.employee_id,
      days: [
        {
          day_of_week_id: [
            workHour.day_of_week?.id || workHour.day_of_week_id,
          ].filter(Boolean),
          is_day_off: workHour.is_day_off === 1 || workHour.is_day_off === true,
          work_start: workHour.work_start || "",
          work_end: workHour.work_end || "",
          lunch_start: workHour.lunch_start || "",
          lunch_end: workHour.lunch_end || "",
        },
      ],
    });
  };

  const cancelEditWorkHour = () => {
    setEditingWorkHour(null);
    resetWorkHours();
  };

  return {
    register,
    handleSubmit,
    onSubmit,
    reset: resetWorkHours, // Usar nossa função customizada
    errors,
    daysOfWeek,
    isSubmitting,
    isEmployeeWorkHours,
    loading,
    error,
    handleDeleteWorkHour,
    setFormData,
    setValue,
    getValues,
    setEmployeeId,
    setDaysOfWeekId,
    watch,
    editingWorkHour,
    startEditWorkHour,
    cancelEditWorkHour,
  };
};

export default useEmployeeWorkHours;
